/**
 * Contacts service - Tenant-wide contacts and project associations
 */
import apiClient from './api';

const contactsService = {
  // ============== CONTACT TYPES ==============
  
  /**
   * Get all available contact types
   * @returns {Promise<Array>}
   */
  async getContactTypes() {
    const response = await apiClient.get('/contacts/types');
    return response.data;
  },

  // ============== TENANT-WIDE CONTACTS ==============
  
  /**
   * Get all contacts for the tenant
   * @param {object} params - Optional filters
   * @returns {Promise<Array>}
   */
  async getAll(params = {}) {
    const response = await apiClient.get('/contacts', { params });
    return response.data;
  },

  /**
   * Get a single contact by ID
   * @param {string} id
   * @returns {Promise<object>}
   */
  async getById(id) {
    const response = await apiClient.get(`/contacts/${id}`);
    return response.data;
  },

  /**
   * Create a new contact
   * @param {object} data
   * @returns {Promise<object>}
   */
  async create(data) {
    const response = await apiClient.post('/contacts', data);
    return response.data;
  },

  /**
   * Update a contact
   * @param {string} id
   * @param {object} data
   * @returns {Promise<object>}
   */
  async update(id, data) {
    const response = await apiClient.put(`/contacts/${id}`, data);
    return response.data;
  },

  /**
   * Delete a contact
   * @param {string} id
   * @returns {Promise<object>}
   */
  async delete(id) {
    const response = await apiClient.delete(`/contacts/${id}`);
    return response.data;
  },

  // ============== PROJECT CONTACTS ==============
  
  /**
   * Get all contacts for a project
   * @param {string} projectId
   * @returns {Promise<Array>}
   */
  async getProjectContacts(projectId) {
    const response = await apiClient.get(`/contacts/project/${projectId}`);
    return response.data;
  },

  /**
   * Add a contact to a project
   * @param {string} projectId
   * @param {object} data - { contact_id, role, is_primary }
   * @returns {Promise<object>}
   */
  async addToProject(projectId, data) {
    const response = await apiClient.post(`/contacts/project/${projectId}`, data);
    return response.data;
  },

  /**
   * Set a contact as primary for a project
   * @param {string} projectId
   * @param {string} contactId
   * @returns {Promise<object>}
   */
  async setPrimaryContact(projectId, contactId) {
    const response = await apiClient.put(`/contacts/project/${projectId}/${contactId}/primary`);
    return response.data;
  },

  /**
   * Remove a contact from a project
   * @param {string} projectId
   * @param {string} contactId
   * @returns {Promise<object>}
   */
  async removeFromProject(projectId, contactId) {
    const response = await apiClient.delete(`/contacts/project/${projectId}/${contactId}`);
    return response.data;
  },
};

export default contactsService;
